/**
 * api/persons.ts —— 人员（§五 5.3）
 *
 * ⚠ **不许传 `isAdmin`**：它是 `type === 'admin'` 的派生值，表单里也没有这个勾选框（红线 4）。
 * ⚠ **不许传 `teamIds`**：队伍归属走队伍管理的「管理成员」——管理后台管档案，队伍管理管关系。
 * ⚠ 停用 / 启用【走独立接口】，不要塞进 `updatePerson`：那条路上有「队长不可停用」的独有校验。
 */
// ⚠ 没有 del：人员【不提供删除】，停用取代删除（历史打卡 / OKR / 周报记录必须保留）
import { get, post, put, type ReqOptions } from './client.js';
import type {
  Paged,
  Person,
  PersonAdminView,
  PersonCreateInput,
  PersonCreateResult,
  PersonSortKey,
  PersonType,
  PersonUpdateInput,
} from '@shared-types';

export interface PersonListQuery {
  type?: PersonType | '';
  /** 'on' = 生效、'off' = 已停用、'' = 全部 */
  active?: 'on' | 'off' | '';
  teamId?: string;
  q?: string;
  sort?: PersonSortKey;
  dir?: 'asc' | 'desc';
}

/** ⚠ 空值不进查询串：`?type=` 这种空参数会让后端与日志都多一层噪音 */
function qs(q: PersonListQuery): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v) p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

/** 通用列表（登录）。⚠ 一次返回全部（106 人规模不分页，见 §五 5.3 补充 C3） */
export const listPersons = (q: PersonListQuery = {}, opts?: ReqOptions) =>
  get<Paged<Person>>(`/api/persons${qs(q)}`, opts);

export const getPerson = (id: string, opts?: ReqOptions) => get<Person>(`/api/persons/${id}`, opts);

/**
 * 管理后台人员 Tab 的整页数据（§五 5.3 补充 C1，管理员专用）。
 * ⚠ 筛选、排序、队伍 chip、参与次数、部门候选**全部由服务端算好下发**，前端不再算一遍。
 */
export const adminView = (q: PersonListQuery = {}, opts?: ReqOptions) =>
  get<PersonAdminView>(`/api/persons/admin${qs(q)}`, opts);

/** 打卡「正式参与人」候选（本队）。⚠ 不含管理员、不含已停用 */
export const candidates = (teamId: string, opts?: ReqOptions) =>
  get<Person[]>(`/api/persons/candidates?teamId=${encodeURIComponent(teamId)}`, opts);

/**
 * 新增。出参带 `initialPassword`（§五 5.3 补充 C2）。
 * ⚠ 那串明文**只有这一次**：不入库、不写日志，界面必须当场显示给管理员抄走。
 */
export const createPerson = (input: PersonCreateInput) =>
  post<PersonCreateResult>('/api/persons', input);

/** 编辑档案。⚠ 出参同样可能带 `initialPassword`——外部导师改回可登录类型时会补建凭据 */
export const updatePerson = (id: string, input: PersonUpdateInput) =>
  put<PersonCreateResult>(`/api/persons/${id}`, input);

/** 停用 / 启用。⚠ 队长不可停用，服务端会拒 */
export const setPersonActive = (id: string, active: boolean) =>
  put<Person>(`/api/persons/${id}/active`, { active });

/**
 * 管理员重置他人密码（§五 5.1）。
 * ⚠ 走 auth 那条接口，不在 persons 下另开一个——密码相关的动作全部收在 5.1。
 */
export const resetPassword = (personId: string) =>
  post<{ initialPassword: string }>('/api/auth/password/reset', { personId });
